import { Injectable } from '@angular/core';
import { Subject, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';
import { Teacher } from '../../models/teacher';
import { Course } from '../../models/course';
import { Lesson } from '../../models/lesson';
import { v4 } from 'uuid';

import { LessonSong } from 'src/app/models/lesson-song';
import { ParentEducationMoment } from 'src/app/models/parent-education-moment';
import { EventService } from 'src/app/services/event.service';

import { TeacherStore } from 'src/app/stores/teacher.store';
import { Song } from 'src/app/models/song';

@Injectable()
export class TeacherProvider {

  private _teacher: Teacher = Teacher.defaults()

  static dirtyDataEvent = 'dirty-teacher-data'
  
  private alreadyCachingUrls: Set<string> = new Set<string>()
  private syncCount: number = 0

  ngUnsubscribeTeacher = new Subject();

  constructor(private eventService: EventService,
              private teacherStore: TeacherStore) {

    this._teacher = this.teacherStore.getTeacher();
    this.teacherStore.state$
        .pipe(takeUntil(this.ngUnsubscribeTeacher))
        .subscribe(() => {
          this._teacher = this.teacherStore.getTeacher();
        });
  }

  ngOnDestroy(){
    this.ngUnsubscribeTeacher.next(true);
    this.ngUnsubscribeTeacher.complete();
  }

  get teacher(): Teacher {
    return this._teacher;
  }

  // private handleError(error: HttpErrorResponse) {
  //   if (error.error instanceof ErrorEvent) {
  //       // A client-side or network error occurred. Handle it accordingly.
  //       console.error('An error occurred:', error.error.message);
  //   } else {
  //       // The backend returned an unsuccessful response code.
  //       // The response body may contain clues as to what went wrong,
  //       console.error(
  //           `Backend returned code ${error.status}, ` +
  //           `body was: ${error.error}`);
  //   }
  //   // return an observable with a user-facing error message
  //   return throwError('Something bad happened; please try again later.');
  // }

  

  saveTeacher(shouldSave: boolean = true) {
    // In here but nothing is happening
    if (shouldSave) {
      this.eventService.publishDirtyTeacherData(this.teacher);
    }
  }


  cacheSongs() {
    let allUrls: string[] = this.getAllSongLinksToCache()
    let urlsToCache: string[] = []
    window.caches.keys().then((values: string[]) => {
      values.forEach((value: string, index: number) => {
        if (value.endsWith('assets:assets:cache')) {
          let cacheNameValue = value
          window.caches.open(cacheNameValue).then(cache => {
            // Remove songs that shouldn't be in cache
            cache.keys().then(function(cacheNames) {
              for (const cacheName of cacheNames) {
                if (!allUrls.includes(cacheName.url)) {
                  // console.log(`Removed from cache: ${cacheName.url}`)
                  cache.delete(cacheName.url).then(() => {})
                }
              }
            })

            // Add songs into cache that aren't there
            allUrls.forEach((url: string) => {
              let found: boolean = false
              cache.keys().then(function(cacheNames) {
                found = cacheNames.some(_ => _.url == url) || urlsToCache.some(_ => _ == url)
                if (!found) {
                  urlsToCache.push(url)
                  // console.log(`Added to cache: ${url}`)
                  try {
                    
                    const request = new Request(url, { mode: 'no-cors' });
                    fetch(request).then(response => cache.put(request, response))
                    // cache.add(request).then(() => {})

                    // cache.add(url).then(() => {})
                  } catch(exc) {
                    
                  }
                  
                }
              })
            })       
          })
        }
      })
    })
  }


  getAllSongLinksToCache(): string[] {
    let allUrls: string[] = []
    this.teacher.courses.forEach((course: Course) => {
      if (this.shouldCacheCourse(course)) {
        course.lessons.forEach((lesson: Lesson) => {
          lesson.songs.forEach((lessonSong: LessonSong) => {
            if (lessonSong.recordingType === LessonSong.RecordingType.Original && lessonSong.url && lessonSong.url.trim().startsWith('https:')) {
              allUrls.push(lessonSong.url)
            }
          })
        })
      }
    })
    allUrls = Array.from((new Set(allUrls)))
    return allUrls
  }

  
  shouldCacheCourse(course: Course): boolean {
    // Per Anne & Deanna, courses will be cached for 6 months from the start time - March 12, 2024 (DW)
    const startDate = new Date(`${course.startDate.month} ${course.startDate.day}, ${course.startDate.year}`)
    let expireDate = new Date()
    expireDate.setMonth(expireDate.getMonth() - 6)
    return startDate > expireDate
  }

  async isCached(url: string): Promise<boolean> {
    if (!url || url?.length == 0) {
      return false
    }
    const cacheKeys: string[] = await window.caches.keys()
    for (const value of cacheKeys) {
      if (value.endsWith('assets:assets:cache')) {
        let cacheNameValue = value
        const cache = await window.caches.open(cacheNameValue)
        const cacheNames = await cache.keys()
        return cacheNames.some(_ => _.url == url)
      }
    }
    return false
  }

  // Adding
  addCourse(course: Course, shouldSave: boolean = true) {
    course.uuid = v4(); //uuid()
    course.isDirty = true
    course.synced = null

    course.lessons.forEach( (lesson: Lesson) => {
      this.addLesson(null, lesson, false)
    })

    this.teacher.courses.push(course)
    this.saveTeacher(shouldSave)
  }

  addLesson(course: Course, lesson: Lesson, shouldSave: boolean = true) {
    lesson.uuid = v4(); //uuid()
    lesson.isDirty = true
    lesson.synced = null

    lesson.songs.forEach( (lessonSong: LessonSong) => {
      this.addLessonSong(null, lessonSong, false)
    })

    // If course is null, then assume is already part of a course.
    // This happens if called from addCourse().
    if (course) {
      course.lessons.push(lesson)
    }

    this.saveTeacher(shouldSave)
  }

  addLessonSong(lesson: Lesson, lessonSong: LessonSong, shouldSave: boolean = true) {
    lessonSong.uuid = v4(); // uuid()
    lessonSong.isDirty = true
    lessonSong.synced = null

    // If lesson is null, then assume lessonSong is already part of a lesson.
    // This happens if called from addLesson().
    if (lesson) {
      lesson.isDirty = true
      lesson.songs.push(lessonSong)
    }

    this.saveTeacher(shouldSave)
  }

  insertLessonSong(lesson: Lesson, lessonSong: LessonSong, index: number, shouldSave: boolean = true) {
    lessonSong.uuid = v4();
    lessonSong.isDirty = true
    lessonSong.synced = null

    if (lesson) {
      lesson.isDirty = true
      lesson.songs.splice(index, 0, lessonSong)
    }

    this.saveTeacher(shouldSave)
  }

  addCustomPem(pem: ParentEducationMoment, shouldSave: boolean = true) {
    pem.uuid = v4();
    pem.isDirty = true
    pem.synced = null

    if (!this.teacher.customPems) {  // TODO: we can remove after customPems are added on server
      this.teacher.customPems = []
    }
    this.teacher.customPems.push(pem)
    // this.saveTeacher(shouldSave)
  }

  // Updating

  updateCourse(course: Course, saveTeacher: boolean = true) {
    const updatedCourse = this.teacher.findCourse(course.uuid)
    if (updatedCourse) {
      updatedCourse.copyFrom(course);
      updatedCourse.isDirty = true;
    }

    this.saveTeacher(saveTeacher)
  }

  updateLesson(course: Course, lesson: Lesson, saveTeacher: boolean = true) {
    const updatedLesson = course.findLesson(lesson.uuid)
    if (updatedLesson) {
      updatedLesson.copyFrom(lesson)
      updatedLesson.isDirty = true
    }

    this.saveTeacher(saveTeacher)
  }

  updateLessonSong(lesson: Lesson, lessonSong: LessonSong, saveTeacher: boolean = true) {
    lesson.isDirty = true  // we sync all of the dirty lessonSongs in the lesson, so need to mark it dirty

    const updatedLessonSong = lesson.findLessonSong(lessonSong.uuid)
    if (updatedLessonSong) {
      updatedLessonSong.copyFrom(lessonSong)
      updatedLessonSong.isDirty = true
    }

    this.saveTeacher(saveTeacher)
  }

  // Deleting
  deleteCourse(course: Course, saveTeacher: boolean = true) {
    course.isDirty = true
    course.isDeleted = true
    this.saveTeacher(saveTeacher)
  }

  deleteLesson(course: Course, lesson: Lesson, saveTeacher: boolean = true) {
    lesson.isDirty = true
    lesson.isDeleted = true
    this.saveTeacher(saveTeacher);
  }

  deleteLessonSong(lesson: Lesson, lessonSong: LessonSong, saveTeacher: boolean = true) {
    lesson.isDirty = true
    lessonSong.isDirty = true
    lessonSong.isDeleted = true

    lesson.songs = lesson.songs.filter(song => song !== lessonSong)

    this.saveTeacher(saveTeacher)
  }

  adjustNumSongs(lesson: Lesson, possibleCounts: number[]) {
    if (possibleCounts) {
        const minCount = Math.min(...possibleCounts)
        const maxCount = Math.max(...possibleCounts)
        if (!possibleCounts.includes(lesson.numSongs)) {
          if (lesson.numSongs < minCount) {
              lesson.numSongs = minCount
          } else if (lesson.numSongs > maxCount) {
              lesson.numSongs = maxCount
          } else {
            lesson.numSongs = minCount
          }
        }
    }
    if (lesson.numSongs === lesson.songs.length) {
        return
    }
    if (lesson.numSongs > lesson.songs.length) {
        while (lesson.numSongs > lesson.songs.length) {
            this.insertLessonSong(lesson, LessonSong.defaults(), lesson.songs.length - 1, false)
        }
    } else {
      const count = lesson.songs.length - lesson.numSongs
      for (let i = 0; i < count; i++) {
        const emptySong = lesson.songs.reverse().find((song: LessonSong) => !song.name)
        if (emptySong) {
          this.deleteLessonSong(lesson, emptySong, false)
        }
      }
    }
  }





  // I've tried two implmementations for caching songs. Both rely on service workers. The first
  // uses the Audio API to hit the url causing it to be cached. The second uses the Cache API.
  // The Audio API approach seems to hang Safari. Leaving both here just in case.
  // NOTE: you need to add the prefix into the ngsw-config.json file or the caching won't
  // work on iOS Safari.

  // private cacheQueue: string[] = []
  //
  // private cacheSongs() {
  //   if (this.cacheQueue.length > 0) {
  //     console.log('dont cache')
  //     console.log(this.cacheQueue)
  //     return
  //   }
  //
  //   this.teacher.courses.forEach((course: Course) => {
  //     course.lessons.forEach((lesson: Lesson) => {
  //       lesson.songs.forEach((song: LessonSong) => {
  //         if (song.url) {
  //           this.cacheQueue.push(song.url)
  //         }
  //         if (song.karaokeUrl) {
  //           this.cacheQueue.push(song.karaokeUrl)
  //         }
  //       })
  //     })
  //   })
  //
  //   this.cacheNextSong()
  // }
  //
  // private cacheNextSong() {
  //   const audio = new Audio(this.cacheQueue.shift())
  //   audio.addEventListener('loadeddata', () => {
  //     console.log(audio.src)
  //     console.log(audio.srcObject)
  //     setTimeout(() => {
  //       this.cacheNextSong()
  //     }, 250)
  //   })
  //
  //   // setTimeout(() => {
  //   //   const _ = new Audio(this.cacheQueue.shift())
  //   //   this.cacheNextSong()
  //   // }, 1000)
  // }


}
